/* eslint-disable @typescript-eslint/naming-convention */
import dayjs from 'dayjs';
import {
  Chip,
  IconButton,
  MenuItem,
  Select,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useState } from 'react';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import {
  FlexBox,
  TableCellCustom,
  TableCellWithBorder,
} from '../../../components/tableItems';
import { SelectOutOfOffice } from '../../../components/inputs';
import WeeklyLogNoteModal from '../Components/WeeklyLogNoteModal';
import WeeklyLogSingleForm from '../Components/WeeklyLogSingleForm';
import useWeeklyLogStore from '../../../stores/weeklyLog/WeeklyLogStore';
import { outOfficeValuesArr, weekendDaysArr } from '../WeeklyLogModel';

const WeeklyLogTableRows = () => {
  const {
    formWeeklyLog,
    updateFormWeeklyLog,
    weeklyLogFetchResponse,
    weeklyLogAverageTotalResponse,
  } = useWeeklyLogStore();
  const [note, setNote] = useState('');
  const [noteIndex, setNoteIndex] = useState(0);
  const [noteModal, setNoteModal] = useState(false);
  const openNoteModal = () => setNoteModal(true);
  const closeNoteModal = () => setNoteModal(false);

  const borderColorRed = (
    day: string,
    outOfficeLog: string,
    value: string | null,
  ) => {
    if (value === null) {
      if (
        weekendDaysArr.includes(day) &&
        outOfficeValuesArr.includes(outOfficeLog)
      ) {
        return 'red';
      }
      if (
        weekendDaysArr.includes(day) &&
        !outOfficeValuesArr.includes(outOfficeLog)
      ) {
        return '';
      }
      return 'red';
    }
    return '';
  };

  const transformToString = (value: number | null) => {
    if (value === 0 || value === null) return null;

    return value.toString();
  };

  return (
    <>
      {weeklyLogFetchResponse.map((log, index) => {
        const { follow_ups, responses_sent, notes, out_of_office } =
          formWeeklyLog[index];
        return (
          <TableRow
            key={log.id}
            sx={{
              backgroundColor:
                weekendDaysArr.includes(log.day_name) &&
                !outOfficeValuesArr.includes(out_of_office ?? '')
                  ? '#E8E8E8'
                  : '',
            }}
          >
            <TableCellWithBorder align="center">
              {log.day_name}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {dayjs(log.date).format('MM-DD-YYYY')}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.new_sites_prospected}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.domain_exception_outreach}
            </TableCellWithBorder>

            <TableCellWithBorder align="center">
              <Chip
                label={log.initial_outreach}
                sx={{ backgroundColor: log.initial_outreach_color ?? '' }}
              />
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.status !== 'completed' && (
                <FlexBox
                  justifyContent="center"
                  alignItems="center"
                  display="inline-block"
                >
                  <Typography fontSize={14} marginRight={1.5}>
                    {follow_ups === null ? '0' : follow_ups}
                  </Typography>
                  <WeeklyLogSingleForm
                    title="Follow Ups"
                    index={index}
                    borderColor={borderColorRed(
                      log.day_name,
                      out_of_office ?? '',
                      transformToString(follow_ups),
                    )}
                    disabled={
                      weekendDaysArr.includes(log.day_name) &&
                      !outOfficeValuesArr.includes(out_of_office ?? '')
                    }
                    day={`${log.day_name} ${dayjs(log.date).format(
                      'MM-DD-YYYY',
                    )}`}
                  />
                </FlexBox>
              )}
              {log.status === 'completed' && (
                <>{log.follow_ups === null ? '0' : log.follow_ups}</>
              )}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.responses_received}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.status !== 'completed' && (
                <FlexBox
                  justifyContent="center"
                  alignItems="center"
                  display="inline-block"
                >
                  <Typography fontSize={14} marginRight={1.5}>
                    {responses_sent === null ? '0' : responses_sent}
                  </Typography>
                  <WeeklyLogSingleForm
                    title="Responses sent"
                    index={index}
                    borderColor={borderColorRed(
                      log.day_name,
                      out_of_office ?? '',
                      transformToString(responses_sent),
                    )}
                    disabled={
                      weekendDaysArr.includes(log.day_name) &&
                      !outOfficeValuesArr.includes(out_of_office ?? '')
                    }
                    day={`${log.day_name} ${dayjs(log.date).format(
                      'MM-DD-YYYY',
                    )}`}
                  />
                </FlexBox>
              )}

              {log.status === 'completed' && (
                <>{log.responses_sent === null ? '0' : log.responses_sent}</>
              )}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.approves}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.opens}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.closes}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.email_opened}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              {log.unsubscribe}
            </TableCellWithBorder>
            <TableCellWithBorder align="center">
              <Select
                value={out_of_office}
                name={`out_of_office-${log.id}`}
                id={`out_of_office-${log.id}`}
                disabled={false}
                displayEmpty
                fullWidth
                onChange={e => {
                  updateFormWeeklyLog(index, {
                    out_of_office: e.target.value,
                  });
                }}
                sx={{ fontSize: 12 }}
                IconComponent={KeyboardArrowDownIcon}
                size="small"
                input={<SelectOutOfOffice />}
              >
                <MenuItem value="n/a" sx={{ fontSize: 12 }}>
                  N/A
                </MenuItem>
                <MenuItem value="full" sx={{ fontSize: 12 }}>
                  Full
                </MenuItem>
                <MenuItem value="half" sx={{ fontSize: 12 }}>
                  Half
                </MenuItem>
              </Select>
            </TableCellWithBorder>
            <TableCellWithBorder>
              {notes && notes !== '' && (
                <>
                  {notes.length > 20 && (
                    <FlexBox>
                      <Typography fontSize={12}>
                        {notes.slice(0, 20)}...
                      </Typography>
                      <Tooltip title={notes}>
                        <IconButton>
                          <HelpIcon />
                        </IconButton>
                      </Tooltip>
                    </FlexBox>
                  )}
                  {notes.length <= 20 && (
                    <Typography fontSize={12}>{notes}</Typography>
                  )}
                </>
              )}
              {(!notes || notes === '') && (
                <IconButton
                  aria-label="filter"
                  id="long-button"
                  sx={{
                    border: 1,
                    borderRadius: '10%',
                  }}
                  aria-haspopup="true"
                  onClick={() => {
                    setNote(notes ?? '');
                    setNoteIndex(index);
                    openNoteModal();
                  }}
                  disabled={log.status === 'completed'}
                  size="small"
                >
                  <ModeEditIcon
                    sx={{
                      fontSize: 14,
                      color: 'black',
                    }}
                  />
                </IconButton>
              )}
            </TableCellWithBorder>
          </TableRow>
        );
      })}
      <TableRow>
        <TableCellCustom align="center" sx={{ fontWeight: 600 }}>
          Total
        </TableCellCustom>
        <TableCellCustom />
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.new_sites_prospected}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.domain_exception_outreach}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.initial_outreach}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.follow_ups}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.responses_received}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.responses_sent}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.approves}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.opens}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.closes}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.email_opened}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.totals.unsubscribe}
        </TableCellWithBorder>
        <TableCellCustom />
        <TableCellCustom />
      </TableRow>
      <TableRow>
        <TableCellCustom align="center" sx={{ fontWeight: 600 }}>
          Average
        </TableCellCustom>
        <TableCellCustom />
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.new_sites_prospected}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.domain_exception_outreach}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.initial_outreach}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.follow_ups}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.responses_received}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.responses_sent}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.approves}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.opens}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.closes}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.email_opened}
        </TableCellWithBorder>
        <TableCellWithBorder align="center">
          {weeklyLogAverageTotalResponse.avg.unsubscribe}
        </TableCellWithBorder>
        <TableCellCustom />
        <TableCellCustom />
      </TableRow>
      <WeeklyLogNoteModal
        open={noteModal}
        index={noteIndex}
        closeModal={closeNoteModal}
        body={note}
      />
    </>
  );
};

export default WeeklyLogTableRows;
